// @djsem/shared — Release Campaign Planner (36), Post-Show Recorder (37),
// Library Cleanup (38), Pro SDK (39), Marketplace (35).
//
// Five modules in one file, each clearly delimited. All follow established
// contract + service patterns. No UI or backend routes — those are dependency-reported.

import { type DJSEMValidationResult, toDJSEMValidationResult, type ValidationResult } from "../index";
import { SeqCounter } from "../runtime";
const AT = "1970-01-01T00:00:00.000Z";
const _seq = new SeqCounter();

// ============================================================================
// PROMPT 39 — Pro SDK / Developer Docs foundation
// ============================================================================

export interface DJSEMProSdkConfig {
  endpoint?: string;
  mode: "dry_run" | "local_replay" | "future_live";
  apiKey?: string; // never sent to external unless future_live + explicit config
  warnings: string[];
}

export interface DJSEMEventBusMessage {
  id: string;
  category: string;
  priority: number; // lower = higher priority; 0 = blackout/safety
  timestamp: string;
  payload: unknown;
  traceId?: string;
  correlationId?: string;
}

export interface DJSEMTestCueResult {
  cueId: string;
  dryRun: boolean;
  outputEvents: DJSEMEventBusMessage[];
  warnings: string[];
}

export interface DJSEMReplayResult {
  fixtureId: string;
  eventsReplayed: number;
  dryRun: boolean;
  warnings: string[];
}

export class DJSEMProSdkClient {
  private handlers: Array<(e: DJSEMEventBusMessage) => void> = [];
  private connected = false;

  get isConnected(): boolean { return this.connected; }

  validateEvent(event: DJSEMEventBusMessage): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (!event.id) issues.push({ path: "$.id", message: "required" });
    if (!event.category) issues.push({ path: "$.category", message: "required" });
    if (event.priority === undefined || event.priority < 0) issues.push({ path: "$.priority", message: "must be >= 0" });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "event");
  }

  async connect(config: DJSEMProSdkConfig): Promise<void> {
    if (config.mode === "future_live") throw new Error("Live mode not implemented — dry_run and local_replay only.");
    this.connected = true;
  }

  async disconnect(): Promise<void> { this.connected = false; this.handlers = []; }

  subscribe(handler: (e: DJSEMEventBusMessage) => void): void { this.handlers.push(handler); }

  /** Emit event to all handlers (for replay/test). No real hardware output. */
  emit(event: DJSEMEventBusMessage): void {
    for (const h of this.handlers) h(event);
  }

  async runTestCue(cueId: string): Promise<DJSEMTestCueResult> {
    const event: DJSEMEventBusMessage = { id: `ev_${_seq.next()}`, category: "cue", priority: 10, timestamp: AT, payload: { cueId }, traceId: `trace_${cueId}` };
    this.emit(event);
    return { cueId, dryRun: true, outputEvents: [event], warnings: ["Dry-run only — no real hardware output."] };
  }

  async replayFixture(events: DJSEMEventBusMessage[]): Promise<DJSEMReplayResult> {
    for (const e of events) this.emit(e);
    return { fixtureId: `fix_${_seq.next()}`, eventsReplayed: events.length, dryRun: true, warnings: [] };
  }
}
